/**
 * Generic E-Signing Review
 */
export default class ESigningReview {
    constructor() {
        // Prevents class being used outside of project scope to avoid errors
        if (document.getElementsByClassName('esigning-agreement').length < 1) {
            return;
        }

        this.initReviewData();
    }

    /**
     * @description Initialize review data
     * @returns void
     */
    initReviewData() {
        const customerNumber = document.getElementById('customerNumber')?.value;
        const customerName = document.getElementById('customerName')?.value;

        if (customerNumber && customerName) {
            document.getElementById('reviewCustomerNumberName').textContent =
                `${customerNumber} ${customerName}`;
        }
    }

    /**
     * @description Update review information from form data
     * @returns void
     */
    updateReviewData() {
        // Update template
        const templateElement = document.querySelector('#signingTemplates .showcase a');
        if (templateElement) {
            document.getElementById('reviewSigningTemplate').textContent =
                templateElement.textContent;
        }

        // Update signing expiry date time (duration days + current time)
        // If deadlineTime exists, keep the calculated date but use deadlineTime as clock time.
        const reviewSigningExpiryDateTime = document.getElementById('reviewSigningExpiryDateTime');
        if (reviewSigningExpiryDateTime) {
            const signingDurationDays = this.getSelectedTemplateDurationDays();
            const signingExpiryDateTime = new Date();
            signingExpiryDateTime.setDate(signingExpiryDateTime.getDate() + signingDurationDays);

            const deadlineTime = (document.getElementById('deadlineTime')?.value || '').trim();
            const deadlineTimeMatch = deadlineTime.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
            if (deadlineTimeMatch) {
                const hours = parseInt(deadlineTimeMatch[1], 10);
                const minutes = parseInt(deadlineTimeMatch[2], 10);
                signingExpiryDateTime.setHours(hours, minutes, 0, 0);
            }

            reviewSigningExpiryDateTime.textContent = this.formatDateTime(signingExpiryDateTime);
        }

        // Update receiver info
        const receiverName = document.getElementById('ReceiverName').value;
        const receiverEmail = document.getElementById('ReceiverEmail').value;
        const receiverPhone = document.getElementById('ReceiverPhoneNumber').value;
        const receiverPersonalInput = document.getElementById('ReceiverPersonalNumber');
        const receiverPersonalRaw = receiverPersonalInput?.dataset?.fromDropdown === 'true'
            ? receiverPersonalInput.dataset.rawValue || ''
            : receiverPersonalInput?.value || '';
        const receiverPersonal = receiverPersonalInput?.dataset?.fromDropdown === 'true'
            ? this.maskPersonalNumberLastFour(receiverPersonalRaw)
            : receiverPersonalRaw;

        document.getElementById('reviewReceiverName').textContent = receiverName || '';
        document.getElementById('reviewReceiverEmail').textContent = receiverEmail || '';
        document.getElementById('reviewReceiverPhone').textContent = receiverPhone || '';
        document.getElementById('reviewReceiverPersonalNumber').textContent = receiverPersonal || '';

        this.updateSellerReviewData();
        this.updateVariantReviewData();
    }

    updateSellerReviewData() {
        const section = document.getElementById('reviewSellerSection');
        if (!section) {
            return;
        }

        const includeSellerSigner = document.getElementById('step2View')?.dataset.includeSellerSigner === 'true';
        section.hidden = !includeSellerSigner;
        section.style.display = includeSellerSigner ? '' : 'none';
        section.setAttribute('aria-hidden', includeSellerSigner ? 'false' : 'true');

        if (!includeSellerSigner) {
            return;
        }

        const sellerName = document.getElementById('CurrentSignerName')?.value || '';
        const sellerEmail = document.getElementById('CurrentSignerEmail')?.value || '';
        const sellerPhone = document.getElementById('CurrentSignerPhoneNumber')?.value || '';
        const sellerPersonal = document.getElementById('CurrentSignerPersonalNumber')?.value || '';

        document.getElementById('reviewSellerName').textContent = sellerName;
        document.getElementById('reviewSellerEmail').textContent = sellerEmail;
        document.getElementById('reviewSellerPhone').textContent = sellerPhone;
        document.getElementById('reviewSellerPersonalNumber').textContent = sellerPersonal;
    }

    updateVariantReviewData() {
        const formContentContainer = document.getElementById('esigning-form-content-container');
        const reviewWrapper = document.getElementById('reviewVariantFieldsWrapper');
        const reviewContainer = document.getElementById('reviewVariantFields');

        if (!formContentContainer || !reviewWrapper || !reviewContainer) {
            return;
        }

        const fields = Array.from(
            formContentContainer.querySelectorAll('.form-element.step1[data-review-label]')
        );

        const reviewEntries = fields
            .map((field) => this.getFieldReviewEntry(field))
            .filter((entry) => entry.label && entry.value);

        const agreementPriceEntries = this.getAgreementPriceReviewEntries();
        reviewEntries.push(...agreementPriceEntries);

        if (!reviewEntries.length) {
            reviewContainer.innerHTML = '';
            reviewWrapper.classList.add('hidden');
            return;
        }

        const fragment = document.createDocumentFragment();
        reviewEntries.forEach((entry) => {
            const row = document.createElement('div');
            row.className = 'layout layout--small lm__row-margin';

            const item = document.createElement('div');
            item.className = 'layout__item u-1/1';

            if (entry.href) {
                const table = document.createElement('table');
                const tr = document.createElement('tr');

                const labelTd = document.createElement('td');
                labelTd.setAttribute('valign', 'top');
                labelTd.setAttribute('width', '90');
                labelTd.className = 'lm__control-label no-margin';
                labelTd.style.fontSize = '16px';
                labelTd.style.paddingLeft = '0px';
                labelTd.textContent = `${entry.label}:`;

                const valueTd = document.createElement('td');
                valueTd.style.fontSize = '16px';
                valueTd.style.paddingLeft = '0px';

                const link = document.createElement('a');
                link.className = 'u-force-word-break';
                if (entry.linkId) {
                    link.id = entry.linkId;
                }
                link.href = entry.href;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.textContent = entry.value;

                valueTd.appendChild(link);
                tr.appendChild(labelTd);
                tr.appendChild(valueTd);
                table.appendChild(tr);
                item.appendChild(table);
                row.appendChild(item);
                fragment.appendChild(row);
                return;
            }

            const label = document.createElement('span');
            label.className = 'lm__control-label no-margin';
            label.textContent = `${entry.label}:`;

            const value = document.createElement('span');
            value.className = 'no-margin';
            value.textContent = ` ${entry.value}`;

            item.appendChild(label);
            item.appendChild(value);
            row.appendChild(item);
            fragment.appendChild(row);
        });

        reviewContainer.innerHTML = '';
        reviewContainer.appendChild(fragment);
        reviewWrapper.classList.remove('hidden');
    }

    getFieldReviewEntry(field) {
        const label = field.dataset.reviewLabel || '';
        const value = this.getFieldReviewValue(field);

        if (!label || !value) {
            return { label: '', value: '' };
        }

        if (field.type === 'file' && field.dataset.reviewAsLink === 'true' && field.files?.length > 0) {
            return {
                label,
                value,
                href: URL.createObjectURL(field.files[0]),
                linkId: field.dataset.reviewLinkId || '',
            };
        }

        return { label, value };
    }

    /**
     * @description One price entry per premium product-item dropdown present in the form -
     * a single entry for the regular premium variants, or one per section for the combined form.
     * @returns {Array<{label: string, value: string}>}
     */
    getAgreementPriceReviewEntries() {
        const productItemInputs = Array.from(
            document.querySelectorAll('input[id^="premiumProductItem"][data-price]')
        );

        return productItemInputs
            .map((productItemInput) => {
                const suffix = productItemInput.id.replace('premiumProductItem', '');
                const priceRaw = productItemInput.dataset?.price;
                if (!priceRaw) {
                    return null;
                }

                const formattedPrice = this.formatPrice(priceRaw);
                if (!formattedPrice) {
                    return null;
                }

                const label = (document.getElementById('agreementReviewPriceLabel' + suffix)?.value || '').trim();
                const unit = (document.getElementById('agreementReviewPriceUnit' + suffix)?.value || '').trim();
                if (!label || !unit) {
                    return null;
                }

                return {
                    label,
                    value: `${formattedPrice} ${unit}`,
                };
            })
            .filter(Boolean);
    }

    getFieldReviewValue(field) {

        // if reviewValue is provided, use it directly.
        // This is for special cases where the display another value in review than
        // the value we will send to backend.
        const reviewValueOverride = (field?.dataset?.reviewValue || '').trim();
        if (reviewValueOverride) {
            return reviewValueOverride;
        }

        const rawValue = field.type === 'file'
            ? (field.files?.[0]?.name || '').trim()
            : (field.value || '').trim();
        if (!rawValue) {
            return '';
        }

        const selector = field.dataset.reviewTextSelector;
        if (!selector) {
            return rawValue;
        }

        const textSource = document.querySelector(selector);
        const displayValue = (textSource?.textContent || '').trim();
        return displayValue || rawValue;
    }

    getSelectedTemplateDurationDays() {
        const durationFromHidden = parseInt(document.getElementById('signingDuration')?.value, 10);
        if (!Number.isNaN(durationFromHidden) && durationFromHidden >= 0) {
            return durationFromHidden;
        }

        const selectedTemplateElement = document.querySelector('#selectTemplate li.selected');
        const durationFromSelectedTemplate = parseInt(selectedTemplateElement?.dataset?.duration, 10);
        if (!Number.isNaN(durationFromSelectedTemplate) && durationFromSelectedTemplate >= 0) {
            return durationFromSelectedTemplate;
        }

        return 0;
    }

    formatDateTime(dateTime) {
        return dateTime.toLocaleString('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    formatPrice(priceRaw) {
        const parsedPrice = Number.parseFloat(priceRaw);
        if (Number.isNaN(parsedPrice)) {
            return '';
        }

        return parsedPrice.toLocaleString('sv-SE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }

    /**
     * @description Mask last four digits of personal number
     * @param {string} personalNumber
     * @returns {string}
     */
    maskPersonalNumberLastFour(personalNumber) {
        return (personalNumber || '').replace(/(\d{8}-)\d{4}$/, '$1****');
    }
}
